import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Bell, BellOff, Monitor, MonitorOff, Volume2 } from 'lucide-react';
import type { OrderQueueCounts } from '@hutdrop/shared';
import { haptic } from '../lib/haptics';
import { playChime, unlockAudio, useAudioUnlocked, usePref, useWakeLock } from '../lib/orderAlert';

/** How often the chime repeats while a new order waits to be accepted. */
const REPEAT_MS = 6_000;

/**
 * Shown on every dashboard page. While pre-orders are waiting to be accepted it shows a banner and,
 * if sound is on, chimes every few seconds, like a delivery tablet, until someone accepts them or
 * presses Silence. A newer order starts it ringing again.
 */
export function NewOrderAlert({ counts }: { counts: OrderQueueCounts | undefined }) {
  const [sound] = usePref('sound');
  const unlocked = useAudioUnlocked();
  const [silencedThrough, setSilencedThrough] = useState<string | null>(null);
  const waiting = counts?.new ?? 0;
  const newest = counts?.newestNewAt ?? null;
  const ringing = waiting > 0 && newest !== null && (silencedThrough === null || newest > silencedThrough);

  useEffect(() => {
    if (!ringing || !sound || !unlocked) return;
    const alert = () => {
      playChime();
      haptic([200, 100, 200]);
    };
    alert();
    const id = setInterval(alert, REPEAT_MS);
    return () => clearInterval(id);
  }, [ringing, sound, unlocked]);

  const needsTap = sound && !unlocked;

  if (waiting === 0) {
    return needsTap ? (
      <button type="button" className="order-alert order-alert--quiet" onClick={unlockAudio}>
        <Volume2 size={18} aria-hidden="true" />
        <span>Tap here to turn on the new-order sound on this device.</span>
      </button>
    ) : null;
  }

  return (
    <div className={`order-alert${ringing ? ' order-alert--ringing' : ''}`} role="alert">
      <Bell size={20} aria-hidden="true" className="order-alert__bell" />
      <span className="order-alert__text">
        <strong>
          {waiting} new pre-order{waiting === 1 ? '' : 's'}
        </strong>{' '}
        waiting to be accepted
      </span>
      <div className="order-alert__actions">
        {needsTap ? (
          <button type="button" className="btn btn--sm btn--secondary" onClick={unlockAudio}>
            <Volume2 size={14} aria-hidden="true" /> Turn on sound
          </button>
        ) : null}
        {ringing && sound && unlocked ? (
          <button type="button" className="btn btn--sm btn--secondary" onClick={() => setSilencedThrough(newest)}>
            <BellOff size={14} aria-hidden="true" /> Silence
          </button>
        ) : null}
        <Link to="/orders?queue=new" className="btn btn--sm btn--primary">
          View new orders
        </Link>
      </div>
    </div>
  );
}

/** Sidebar switches for this device: the order sound and keeping the screen on. */
export function CounterSwitches() {
  const [sound, setSound] = usePref('sound');
  const [awake, setAwake] = usePref('awake');
  const { supported: canStayAwake } = useWakeLock(awake);
  return (
    <div className="counter-switches">
      <button
        type="button"
        className="counter-switch"
        aria-pressed={sound}
        onClick={() => {
          if (!sound) {
            unlockAudio();
            playChime();
          }
          setSound(!sound);
        }}
      >
        {sound ? <Bell size={16} aria-hidden="true" /> : <BellOff size={16} aria-hidden="true" />}
        <span>Order sound {sound ? 'on' : 'off'}</span>
      </button>
      {canStayAwake ? (
        <button type="button" className="counter-switch" aria-pressed={awake} onClick={() => setAwake(!awake)}>
          {awake ? <Monitor size={16} aria-hidden="true" /> : <MonitorOff size={16} aria-hidden="true" />}
          <span>Keep screen on {awake ? 'on' : 'off'}</span>
        </button>
      ) : null}
    </div>
  );
}
